import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, RefreshCw, Eye, Search, Filter, MoreVertical, ChevronDown } from 'lucide-react';
import { apiFetch } from '../services/api';
import './Cobrancas.css';

export default function CobrancasAssinaturas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Verificação do Atendente baseada nas permissões
  const userPermissions = user?.permissions || [];
  const isAtendente = userPermissions.length > 0 && 
                      !userPermissions.includes('administracao') && 
                      !userPermissions.includes('relatorios') &&
                      !userPermissions.includes('gerar-cobranca');
  
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  // Carrega pagamentos automaticamente APENAS se NÃO for Atendente
  useEffect(() => {
    if (isAtendente) {
      setLoading(false);
      setPagamentos([]);
      return;
    }

    const carregarPagamentos = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('/api/asaas/local-payments?limit=500');
        const dados = response.data || [];
        // Filtra apenas assinaturas (SUBSCRIPTION)
        const assinaturas = dados.filter(p =>
          p.subscription_type === 'SUBSCRIPTION' ||
          (p.description && p.description.toLowerCase().includes('assinatura'))
        );
        setPagamentos(assinaturas);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar as assinaturas.');
      } finally {
        setLoading(false);
      }
    };
    carregarPagamentos();
  }, [isAtendente]);

  // Função de busca via API (para Atendente)
  const realizarBusca = async () => {
    const term = searchTerm.trim();
    
    if (!term) {
      setPagamentos([]);
      setBuscaRealizada(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/asaas/local-payments?limit=500&search=${encodeURIComponent(term)}`);
      let dados = response.data || [];
      
      // Filtro manual para garantir resultados relevantes
      const termLower = term.toLowerCase();
      dados = dados.filter(p => {
        const name = (p.customerName || '').toLowerCase();
        const email = (p.customerEmail || '').toLowerCase();
        const cpf = (p.customerCpf || p.cpf || '').replace(/\D/g, '');
        const description = (p.description || '').toLowerCase();
        const termDigits = term.replace(/\D/g, '');
        
        const match = name.includes(termLower) || 
                      email.includes(termLower) || 
                      description.includes(termLower) ||
                      (termDigits && cpf.includes(termDigits));
        
        return match;
      });
      
      // Filtra apenas assinaturas (SUBSCRIPTION)
      const assinaturas = dados.filter(p =>
        p.subscription_type === 'SUBSCRIPTION' ||
        (p.description && p.description.toLowerCase().includes('assinatura'))
      );
      
      setPagamentos(assinaturas);
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      setError('Não foi possível realizar a busca.');
      setPagamentos([]);
      setBuscaRealizada(true);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '—';
    const d = new Date(dataStr + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const getStatusBadge = (status) => {
    const classes = {
      'RECEIVED': 'cob-status-received',
      'CONFIRMED': 'cob-status-confirmed',
      'PENDING': 'cob-status-pending',
      'OVERDUE': 'cob-status-overdue'
    };
    const labels = {
      'RECEIVED': 'Recebido',
      'CONFIRMED': 'Confirmado',
      'PENDING': 'Pendente',
      'OVERDUE': 'Vencido'
    };
    return <span className={`cob-status-badge ${classes[status] || ''}`}>{labels[status] || status}</span>;
  };

  return (
    <div className="cobrancas-page-container">
      <div className="cobrancas-page-header">
        <h1 className="cobrancas-page-title">Assinaturas</h1>
      </div>

      <div className="cobrancas-card">
        {/* Toolbar */}
        <div className="cobrancas-toolbar">
          <div className="cobrancas-toolbar-left">
            <div className="cobrancas-search-box">
              <input
                type="text"
                placeholder={isAtendente ? "Digite o nome ou email do cliente para buscar" : "Procurar por nome ou email do cliente"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    realizarBusca();
                  }
                }}
                className="cobrancas-search-input"
              />
              <button 
                className="cobrancas-search-btn" 
                title="Buscar"
                onClick={realizarBusca}
              >
                <Search size={16} />
              </button>
            </div>
            <button className="cobrancas-btn-filter">
              <Filter size={16} />
              Filtros
              <ChevronDown size={16} />
            </button>
          </div>
          <div className="cobrancas-toolbar-right">
            <button className="cobrancas-btn-batch">
              <MoreVertical size={16} />
              Ações em lote
              <ChevronDown size={16} />
            </button>
            <button className="cobrancas-btn-add" onClick={() => navigate('/gerar-cobranca?tipo=assinatura')}>
              <span style={{ fontSize: 18, marginRight: 4 }}>+</span>
              Adicionar cobrança
            </button>
          </div>
        </div>

        {loading && (
          <div className="cobrancas-loading">
            <RefreshCw size={24} className="spinning" />
            <p>Carregando assinaturas...</p>
          </div>
        )}

        {error && (
          <div className="cobrancas-error">
            <AlertCircle size={24} />
            <p>{error}</p>
          </div>
        )}

        {/* Mensagem inicial para Atendente */}
        {isAtendente && !buscaRealizada && !loading && !error && (
          <div className="cobrancas-empty">
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '12px',
              padding: '40px 20px',
              color: '#64748b'
            }}>
              <Search size={48} style={{ color: '#94a3b8' }} />
              <h3 style={{ margin: 0, color: '#334155', fontSize: '18px' }}>
                Realize uma busca para visualizar as assinaturas
              </h3>
              <p style={{ margin: 0, textAlign: 'center', maxWidth: '400px', fontSize: '14px' }}>
                Digite o nome ou email do cliente no campo de busca acima e pressione Enter ou clique no ícone de lupa.
              </p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && pagamentos.length === 0 && (buscaRealizada || !isAtendente) && (
          <div className="cobrancas-getstarted">
            <div className="cobrancas-getstarted-illustration">
              <svg width="180" height="150" viewBox="0 0 180 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="52" y="28" width="90" height="90" rx="10" fill="#3b82f6" />
                <rect x="52" y="28" width="90" height="24" rx="10" fill="#1d4ed8" />
                <circle cx="72" cy="24" r="7" fill="none" stroke="#1e3a8a" strokeWidth="4" />
                <circle cx="97" cy="24" r="7" fill="none" stroke="#1e3a8a" strokeWidth="4" />
                <circle cx="122" cy="24" r="7" fill="none" stroke="#1e3a8a" strokeWidth="4" />
                <rect x="62" y="62" width="14" height="14" rx="3" fill="#bfdbfe" />
                <rect x="82" y="62" width="14" height="14" rx="3" fill="#bfdbfe" />
                <rect x="102" y="62" width="14" height="14" rx="3" fill="#bfdbfe" />
                <rect x="122" y="62" width="10" height="14" rx="3" fill="#bfdbfe" />
                <rect x="62" y="82" width="14" height="14" rx="3" fill="#bfdbfe" />
                <rect x="82" y="82" width="14" height="14" rx="3" fill="#bfdbfe" />
                <rect x="102" y="82" width="14" height="14" rx="3" fill="#bfdbfe" />
                <path d="M118 118 L132 132 L158 100" stroke="#84cc16" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <circle cx="45" cy="60" r="20" fill="#a3e635" />
                <text x="45" y="67" fontSize="20" fontWeight="700" fill="#1a2e05" textAnchor="middle" fontFamily="Arial, sans-serif">$</text>
              </svg>
            </div>
            <h2 className="cobrancas-getstarted-title">Comece a cobrar seus clientes com o Asaas</h2>
            <p className="cobrancas-getstarted-description">
              As assinaturas são cobranças recorrentes nas quais você escolhe o período em que serão disparadas, ideal para vendas recorrentes.
            </p>
            <button className="cobrancas-getstarted-cta" onClick={() => navigate('/gerar-cobranca?tipo=assinatura')}>
              Criar assinatura
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && pagamentos.length > 0 && (buscaRealizada || !isAtendente) && (
          <div className="cobrancas-table-wrapper">
            <table className="cobrancas-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Descrição</th>
                  <th>Data Pagamento</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentos.map(p => (
                  <tr key={p.id}>
                    <td>{p.customerName || '—'}</td>
                    <td className="valor">{formatarMoeda(p.value)}</td>
                    <td>{formatarData(p.dueDate)}</td>
                    <td>{getStatusBadge(p.status)}</td>
                    <td>{p.description || '—'}</td>
                    <td>{formatarData(p.receivedDate || p.paymentDate)}</td>
                    <td>
                      <button
                        className="cob-btn-view"
                        title="Ver detalhes"
                        onClick={() => window.open(p.invoiceUrl || '#', '_blank')}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}